/**
 * Pure playback helpers for the carpool replay.
 *
 * The page holds one number — the current playback time — and everything shown
 * is derived from it by folding the event log up to that instant. That keeps
 * scrubbing backwards exactly as correct as playing forwards, with no separate
 * "undo" path to get wrong.
 */

export const STATUS_ORDER = ['waiting', 'called', 'sent', 'picked_up'];

export const STATUS_LABELS = {
  waiting: 'Waiting',
  called: 'Called',
  sent: 'Sent to carpool',
  picked_up: 'Picked up',
};

// Event families, used for filtering and for colouring the feed.
export const EVENT_GROUPS = {
  dismissal: ['dismissal.started', 'dismissal.ended', 'dismissal.status.changed'],
  geofence: ['geofence.entered', 'geofence.exited'],
  interaction: ['ui.tap', 'ui.long_press', 'ui.input', 'ui.toggle', 'nav.screen', 'nav.back', 'screen.view'],
  lifecycle: ['app.launch', 'app.state', 'app.terminate', 'socket.connect', 'socket.disconnect', 'net.error'],
  permission: ['parent.permission.granted', 'parent.permission.reported', 'parent.permission.revoked'],
};

// Anything not listed above falls into 'other', which is a real bucket the
// filter has to offer — otherwise a new event type would be silently unfilterable.
export const GROUP_LABELS = {
  dismissal: 'Dismissal',
  geofence: 'Pickup zones',
  interaction: 'Taps & screens',
  lifecycle: 'App & connection',
  permission: 'Permissions',
  other: 'Other',
};

export const ALL_EVENT_GROUPS = [...Object.keys(EVENT_GROUPS), 'other'];

/** Count of events per group, for labelling the filter. */
export function countEventsByGroup(events = []) {
  const counts = Object.fromEntries(ALL_EVENT_GROUPS.map((group) => [group, 0]));
  events.forEach((event) => {
    const group = eventGroup(event.event_type);
    counts[group] = (counts[group] || 0) + 1;
  });
  return counts;
}

export function toMs(value) {
  if (value === null || value === undefined) return null;
  const parsed = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

export function eventGroup(eventType) {
  const match = Object.entries(EVENT_GROUPS).find(([, types]) => types.includes(eventType));
  return match ? match[0] : 'other';
}

/**
 * Sort events into playback order and stamp each with its offset from the start
 * of the run, so the UI can position them without repeating the arithmetic.
 */
export function buildTimeline(events = []) {
  const usable = events
    .map((event) => ({ ...event, ms: toMs(event.occurred_at) }))
    .filter((event) => event.ms !== null)
    .sort((left, right) => (left.ms - right.ms) || ((left.seq || 0) - (right.seq || 0)) || (left.id - right.id));

  if (usable.length === 0) {
    return { events: [], startMs: null, endMs: null, durationMs: 0 };
  }

  const startMs = usable[0].ms;
  const endMs = usable[usable.length - 1].ms;

  return {
    events: usable.map((event) => ({ ...event, offsetMs: event.ms - startMs })),
    startMs,
    endMs,
    durationMs: Math.max(endMs - startMs, 0),
  };
}

/**
 * Widen a timeline to cover the GPS tracks as well.
 *
 * A phone reports breadcrumbs in the background without generating any event,
 * so a parent's drive to the school routinely starts before the first event and
 * can run past the last one. Scrubbing has to cover that, or part of every
 * track sits outside the range the slider can reach.
 */
export function extendRangeWithTracks(timeline, tracks = []) {
  let startMs = timeline.startMs;
  let endMs = timeline.endMs;

  tracks.forEach((track) => {
    const points = track?.points;
    if (!points?.length) return;

    const first = toMs(points[0].t);
    const last = toMs(points[points.length - 1].t);
    if (first !== null && (startMs === null || first < startMs)) startMs = first;
    if (last !== null && (endMs === null || last > endMs)) endMs = last;
  });

  if (startMs === null || endMs === null) return timeline;

  return {
    ...timeline,
    startMs,
    endMs,
    durationMs: Math.max(endMs - startMs, 0),
    // Offsets are measured from the widened start so the density strip and the
    // slider share one origin.
    events: timeline.events.map((event) => ({ ...event, offsetMs: event.ms - startMs })),
  };
}

/** Index of the last event at or before `atMs`, or -1. Binary search: this runs on every frame. */
export function lastEventIndexAt(events, atMs) {
  let low = 0;
  let high = events.length - 1;
  let result = -1;

  while (low <= high) {
    const mid = (low + high) >> 1;
    if (events[mid].ms <= atMs) {
      result = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return result;
}

/**
 * Fold status changes up to `atMs` into the state of every student at that
 * instant — what the teacher board looked like then, and who put it that way.
 */
export function deriveStudentStates(events, atMs) {
  const states = new Map();
  const cutoff = lastEventIndexAt(events, atMs);

  for (let index = 0; index <= cutoff; index += 1) {
    const event = events[index];
    if (event.event_type !== 'dismissal.status.changed') continue;
    if (!event.subject_id) continue;

    const status = event.payload?.status;
    if (!STATUS_ORDER.includes(status)) continue;

    states.set(Number(event.subject_id), {
      student_id: Number(event.subject_id),
      student_name: event.subject_name || `Student ${event.subject_id}`,
      status,
      changed_at: event.occurred_at,
      changed_at_ms: event.ms,
      by_name: event.actor_name || null,
      by_role: event.actor_role || null,
    });
  }

  return states;
}

/**
 * Count of students in each status at `atMs`.
 *
 * `totalStudents` matters: a student who never moved generates no event, so the
 * fold above only knows about students who changed. Without the roster size the
 * waiting count reads 0 for a run where most of the school was still waiting.
 * Everyone unaccounted for is by definition still waiting.
 */
export function summarizeStatuses(states, totalStudents = null) {
  const counts = { waiting: 0, called: 0, sent: 0, picked_up: 0 };
  states.forEach((state) => {
    if (counts[state.status] !== undefined) counts[state.status] += 1;
  });

  if (Number.isFinite(totalStudents) && totalStudents > 0) {
    const accountedFor = counts.called + counts.sent + counts.picked_up;
    counts.waiting = Math.max(totalStudents - accountedFor, counts.waiting);
  }

  return counts;
}

/**
 * Index of the last breadcrumb at or before `atMs`, or -1 if the track has not
 * started yet. Separate from lastEventIndexAt because GPS points carry `t`
 * rather than a precomputed `ms`.
 */
export function lastPointIndexAt(points, atMs) {
  if (!Array.isArray(points) || points.length === 0) return -1;

  let low = 0;
  let high = points.length - 1;
  let result = -1;

  while (low <= high) {
    const mid = (low + high) >> 1;
    const ms = toMs(points[mid].t);
    if (ms !== null && ms <= atMs) {
      result = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return result;
}

/**
 * Where a parent was at `atMs`, interpolating between the two surrounding
 * breadcrumbs so the marker moves smoothly rather than jumping every 10 seconds.
 *
 * Returns null before the track's first point. After the last point it returns
 * that point marked `stale`, because "their phone stopped reporting here" is a
 * finding worth seeing, not a reason to hide the marker.
 */
export function interpolatePosition(points, atMs) {
  if (!Array.isArray(points) || points.length === 0) return null;

  const first = toMs(points[0].t);
  if (first === null || atMs < first) return null;

  let low = 0;
  let high = points.length - 1;
  let index = 0;

  while (low <= high) {
    const mid = (low + high) >> 1;
    if (toMs(points[mid].t) <= atMs) {
      index = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  const current = points[index];
  const next = points[index + 1];
  const currentMs = toMs(current.t);

  if (!next) {
    return {
      lat: current.lat,
      lng: current.lng,
      accuracy: current.accuracy,
      speed: current.speed,
      source: current.source,
      stale: atMs - currentMs > 60000,
      staleForMs: atMs - currentMs,
    };
  }

  const nextMs = toMs(next.t);
  const span = nextMs - currentMs;
  const ratio = span > 0 ? Math.min(Math.max((atMs - currentMs) / span, 0), 1) : 0;

  return {
    lat: current.lat + (next.lat - current.lat) * ratio,
    lng: current.lng + (next.lng - current.lng) * ratio,
    accuracy: current.accuracy,
    speed: current.speed,
    source: current.source,
    stale: false,
    staleForMs: 0,
  };
}

/**
 * A parent's app state at `atMs` — active, backgrounded, or closed.
 *
 * This is the "when do parents close the app" view: it reads the same lifecycle
 * events the phone reported, in order.
 */
export function deriveAppStates(events, atMs) {
  const states = new Map();
  const cutoff = lastEventIndexAt(events, atMs);

  for (let index = 0; index <= cutoff; index += 1) {
    const event = events[index];
    if (!event.actor_id) continue;

    const key = `${event.actor_type}:${event.actor_id}`;

    if (event.event_type === 'app.launch') {
      states.set(key, { state: 'active', at: event.ms, app: event.app });
    } else if (event.event_type === 'app.terminate') {
      states.set(key, { state: 'closed', at: event.ms, app: event.app, reason: event.payload?.reason || null });
    } else if (event.event_type === 'app.state') {
      const to = event.payload?.to;
      states.set(key, {
        state: to === 'active' ? 'active' : 'background',
        at: event.ms,
        app: event.app,
        raw: to,
      });
    }
  }

  return states;
}

/** Everything one actor did, for the single-POV view. */
export function filterEventsForActor(events, actorType, actorId) {
  if (!actorType || !actorId) return events;
  return events.filter((event) => (
    event.actor_type === actorType && Number(event.actor_id) === Number(actorId)
  ));
}

export function formatClock(ms) {
  if (ms === null || ms === undefined) return '—';
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function formatOffset(ms) {
  if (!Number.isFinite(ms) || ms < 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatDuration(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const totalMinutes = Math.round(ms / 60000);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  return `${hours}h ${totalMinutes % 60}m`;
}

/**
 * A one-line, human description of an event for the feed. The replay is read by
 * people, so this is where raw event types become sentences.
 */
export function describeEvent(event) {
  const subject = event.subject_name || (event.subject_id ? `#${event.subject_id}` : null);
  const payload = event.payload || {};

  switch (event.event_type) {
    case 'dismissal.started':
      return `Dismissal started (${payload.student_count ?? '?'} students, ${payload.trigger || 'manual'})`;
    case 'dismissal.ended':
      return `Dismissal ended (${payload.trigger || 'manual'})`;
    case 'dismissal.status.changed':
      return `${subject || 'A student'} → ${STATUS_LABELS[payload.status] || payload.status}`;
    case 'geofence.entered':
      return `Entered pickup zone${payload.called_student_ids?.length ? ` — auto-called ${payload.called_student_ids.length}` : ''}`;
    case 'geofence.exited':
      return `Left pickup zone${payload.completed_student_ids?.length ? ` — completed ${payload.completed_student_ids.length}` : ''}`;
    case 'ui.tap':
      return `Tapped "${payload.label || 'control'}"${subject ? ` on ${subject}` : ''}${payload.disabled ? ' (disabled)' : ''}`;
    case 'nav.screen':
      return `Opened ${payload.to || 'a screen'}${payload.from ? ` (from ${payload.from})` : ''}`;
    case 'app.launch':
      return 'Opened the app';
    case 'app.state':
      return payload.to === 'active' ? 'Returned to the app' : `App went to ${payload.to || 'background'}`;
    case 'app.terminate':
      return `Closed the app${payload.reason ? ` (${payload.reason})` : ''}`;
    case 'socket.connect':
      return 'Live connection established';
    case 'socket.disconnect':
      return `Live connection lost${payload.reason ? ` (${payload.reason})` : ''}`;
    case 'net.error':
      return payload.state === 'offline' ? 'Went offline' : 'Came back online';
    case 'parent.permission.granted':
      return `Granted location permission (${payload.level || 'unknown'})`;
    case 'parent.permission.revoked':
      return `Location permission stopped (${payload.level || 'unknown'})`;
    case 'parent.permission.reported':
      return `Location permission is ${payload.level || 'unknown'}/${payload.accuracy || 'unknown'}`;
    default:
      return event.event_type;
  }
}
